import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useRequest, { RequestTypes } from '../../../hooks/useRequest'
import { IconButton } from '../../../ui'
import { RefuseToApproveModal, SendForRevisionModal } from '../'
import { Alert, message } from 'antd'
import { EApproveType, EUserType } from '../../../../constants/approval'
import { IUserInfo } from '../../../../store/user/interfaces'
import { EAction } from '../../QuestionnaireApprovalItem'
import IAppState from 'store/states'
import { useSelector } from 'react-redux'
import moment from 'moment'

import style from './style.m.less'
import { useCurrentUser } from '../../../hooks/useCurrentUser'
import { signBody } from '../../../helpers'

interface IButtonContainer {
    profile: IUserInfo
    action: EAction
}

const ButtonContainer: FC<IButtonContainer> = ({ profile, action }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const { fetch } = useRequest({
        link: `/api/users/v1/profile-approve`,
        method: RequestTypes.post,
    })

    const fullName =
        profile?.user_type === EUserType.FizLico
            ? `${profile?.last_name} ${profile?.first_name} ${profile?.middle_name}`
            : profile?.user_type === EUserType.IP
            ? `ИП ${profile?.last_name} ${profile?.first_name} ${profile?.middle_name}`
            : profile?.company_name_short

    const messages = {
        1: {
            title: t('questionnaireApprovalItem.success'),
            description: `Анкета пользователя ${fullName} согласована`,
        },
        2: { title: 'Анкета отправлена на доработку' },
        3: {
            title:
                action === EAction.SignUp
                    ? 'В регистрации отказано'
                    : 'В редактировании Анкеты отказано',
        },
    }

    const [isRevisionModalVisible, setIsRevisionModalVisible] = useState(false)
    const openRevisionModal = () => {
        setIsRevisionModalVisible(true)
    }

    const [isRefuseModalVisible, setIsRefuseModalVisible] = useState(false)
    const openRefuseModal = () => {
        setIsRefuseModalVisible(true)
    }

    const userInfo = useSelector((store: IAppState) => store.user)
    const { data } = userInfo
    const { isUserAdmin } = useCurrentUser()
    const isEmptySign = isUserAdmin && !data?.certificate_data
    const isSignExpired =
        isUserAdmin && !isEmptySign && !moment(data?.certificate_data.token_expiration).isAfter()

    const handleRequest = async (
        approveType: EApproveType = EApproveType.Approve,
        comment = '',
    ) => {
        const body = {
            profile_id: profile.id,
            approve_type: approveType,
            profile_comment: comment,
        }
        if (
            isUserAdmin &&
            (approveType == EApproveType.Approve || approveType == EApproveType.Canceled)
        ) {
            const { request_id, signature } = await signBody(body, data)
            fetch({
                body,
                getParams: {
                    request_id,
                },
                config: {
                    headers: {
                        Signature: signature,
                    },
                },
                onSuccess() {
                    navigate('/questionnaire-approval')
                    setIsRevisionModalVisible(false)
                },
                onError(error) {
                    if (error && error.err_code === 'ERR_APL_47') {
                        message.error('В Системе реестра уже используется ЭП с таким сертификатом')
                    }
                },
                successMessage: messages[approveType],
                errorCodeMessage: true,
            })
        } else {
            fetch({
                body,
                onSuccess() {
                    navigate('/questionnaire-approval')
                    setIsRevisionModalVisible(false)
                },
                onError(error) {
                    if (error && error.err_code === 'ERR_APL_47') {
                        message.error('В Системе реестра уже используется ЭП с таким сертификатом')
                    }
                },
                successMessage: messages[approveType],
                errorCodeMessage: true,
            })
        }
    }

    return (
        <>
            {(isEmptySign || isSignExpired) && (
                <Alert
                    className={style.signError}
                    message={isEmptySign ? 'Не установлена ЭП' : 'Истек срок действия ЭП'}
                    type="error"
                    showIcon
                />
            )}
            <div className={style.buttonContainer}>
                <IconButton
                    size="large"
                    className={style.buttonContainerApprove}
                    onClick={openRevisionModal}
                >
                    {t('questionnaireApprovalItem.refineButton')}
                </IconButton>
                <IconButton
                    size="large"
                    className={style.buttonContainerApprove}
                    onClick={() => handleRequest()}
                    disabled={isEmptySign || isSignExpired}
                >
                    {isUserAdmin
                        ? t('questionnaireApprovalItem.signAndApproveButton')
                        : t('questionnaireApprovalItem.approveButton')}
                </IconButton>
                <IconButton
                    size="large"
                    className={style.buttonContainerCancel}
                    onClick={openRefuseModal}
                    disabled={isEmptySign || isSignExpired}
                >
                    {isUserAdmin
                        ? t('questionnaireApprovalItem.signAndCancelButton')
                        : t('questionnaireApprovalItem.cancelButton')}
                </IconButton>

                <SendForRevisionModal
                    isModalVisible={isRevisionModalVisible}
                    onConfirm={(comment) => {
                        handleRequest(EApproveType.SendRevision, comment)
                    }}
                    onCancel={() => setIsRevisionModalVisible(false)}
                />
                <RefuseToApproveModal
                    isModalVisible={isRefuseModalVisible}
                    onConfirm={(comment) => {
                        handleRequest(EApproveType.Canceled, comment)
                    }}
                    onCancel={() => setIsRefuseModalVisible(false)}
                />
            </div>
        </>
    )
}

export default ButtonContainer
